import { Alert, Link, PrimaryButton } from '@janeapp/burrito-design-system';
import { useNavigate } from 'react-router-dom';
import JaneLogoImg from '~/assets/images/jane_logo_200.svg';
import {
  DisciplineInput,
  DISCIPLINE_INPUT_ID,
  LOCATION_INPUT_ID,
} from '~/components';
import { useAppDispatch, useAppSelector } from '~/state';
import { clearDiscipline, selectDiscipline } from '~/state/discipline';
import {
  initializeDefaultLocation,
  resetLocation,
  selectLocation,
} from '~/state/location';
import { trackEvent } from '~/hooks';
import { ReactElement, useEffect } from 'react';
import { resetMapBounds } from '~/state/mapData';
import { hasFeatureFlag } from '@janeapp/feature-flags-client';
import { clearSearchResult } from '~/state/search';

interface SplashPanelProps {
  locationInput: ReactElement;
}

export function SplashPanel({ locationInput }: SplashPanelProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const discipline = useAppSelector(selectDiscipline);
  const location = useAppSelector(selectLocation);

  useEffect(() => {
    //there is no default location for north van beta
    if (hasFeatureFlag('NORTH_VANCOUVER_BETA')) {
      dispatch(resetLocation());
    } else {
      dispatch(initializeDefaultLocation());
      dispatch(resetMapBounds());
    }
    dispatch(clearDiscipline());
    dispatch(clearSearchResult());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAlertContent = () => {
    if (hasFeatureFlag('NORTH_VANCOUVER_BETA')) {
      return (
        <span data-testid="splash-panel-alert-content-north-van">
          Jane’s Practitioner Search is a new product that helps you find and
          book last-minute appointments. Early access is currently available in
          Squamish, West Vancouver, and North Vancouver. Want to see this in
          your area?{' '}
        </span>
      );
    } else {
      return (
        <span data-testid="splash-panel-alert-content-squamish">
          Jane&apos;s Practitioner Search is an experimental way to find and
          book last-minute appointments. Not in Squamish?{' '}
        </span>
      );
    }
  };

  return (
    <div className="flex h-full w-full flex-col justify-between gap-4 overflow-y-auto p-4">
      <div className="flex justify-center">
        <img
          src={JaneLogoImg}
          alt="Logo"
          className="h-16 w-auto"
          width="64"
          height="52"
        />
      </div>
      <div className="flex flex-col gap-3">
        <h1 className="py-4 text-2xl font-semibold">
          Discover practitioners near you
        </h1>
        <Alert>
          <Alert.Content data-testid="splash-panel-alert-content">
            {getAlertContent()}
            <Link
              href="https://forms.gle/YDNu66kj26XY27ja7"
              target="_blank"
              className="font-thin"
              aria-label="Request your city"
            >
              Request your city
            </Link>
          </Alert.Content>
        </Alert>

        <div className="mt-3 flex flex-col gap-2">
          <label
            htmlFor={DISCIPLINE_INPUT_ID}
            className="text-black text-md font-semibold"
          >
            Select a service
          </label>
          <DisciplineInput />
          <label
            htmlFor={LOCATION_INPUT_ID}
            className="text-black text-md font-semibold"
          >
            Select your location
          </label>
          {locationInput}
          <PrimaryButton
            data-testid="search-button"
            className="mt-3 w-full"
            aria-label="Search for practitioners"
            type="button"
            disabled={
              hasFeatureFlag('NORTH_VANCOUVER_BETA')
                ? !discipline || !location // Both required when flag is enabled
                : !discipline // Only discipline required when flag is disabled
            }
            onClick={() => {
              navigate('/search');
              trackEvent('mpl_initial_search_started', {
                discipline,
                location: location?.value,
                url: window.location.pathname,
              });
            }}
          >
            Search
          </PrimaryButton>
        </div>
      </div>
      <div className="text-xs text-neutral-80">
        While Jane offers this Practitioner Search to help you find allied
        health care, each clinic or practitioner listed operates independently
        and is responsible to you for the services and care they offer.&nbsp;
        <b>
          Listing in Jane&apos;s Practitioner Search should not be considered an
          endorsement by Jane of any clinic or practitioner, and Jane will not
          be responsible to you for the services to you by any clinic or
          practitioner listed here.
        </b>{' '}
        Please keep this in mind when booking.
      </div>
    </div>
  );
}
